import { CopyOutlined } from '@ant-design/icons';
import { Flex, Form, Input, notification, Skeleton, Spin } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay, ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { PatientAssessmentLink } from 'lib/documents/patient-assessment/patient-assessment-link';
import {
  useAssessmentLinkAPI,
  useAssessmentLinkGenerateToken,
  useAssessmentLinkListQuery
} from 'lib/hooks/tanstack/use-assessmentLink';
import { useCopyToClipboardMutation } from 'lib/hooks/tanstack/use-CopyToClipboardMutation';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { ErrorListContext, useErrorList } from 'lib/utils/useErrorList';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import { Routes } from 'routes';
import { usePatientInfoContext } from '../../../../patient/_utils/patient-info/hook-context-patientinfo';
import { DisplayAssessmentLinksTable } from './display-assessment-links-table';

export function tokenToInviteLink(token: string): string {
  try {
    const url = new URL(window.location.origin);
    url.pathname = generatePath(Routes.AssessmentLinkEntrypoint, { token });
    return url.toString();
  } catch (e) {
    const _error = resolveError(e);
    const error = new Error(
      `[tokenToInviteLink] Failed to create an invite link for token ('${token}'). ${_error.message}`
    );

    throw error;
  }
}

export function ScheduleTestUI() {
  const mutationActions = useAssessmentLinkAPI();

  const patientInfo = usePatientInfoContext();
  const assesmentLinks = useAssessmentLinkListQuery(
    patientInfo.patientInfo.patient.id
  );
  const errorList = useErrorList();

  function createAssessmentLink() {
    mutationActions.create.mutate(
      {
        patientId: patientInfo.patientInfo.patient.id
      },
      {
        onError: error =>
          errorList.addError(resolveError(error), 'create-actionlink')
      }
    );
  }
  const assessmentLinkData = useMemo(
    () =>
      sortBy(
        assesmentLinks.data ?? [],
        a => -(a.metadata?.created.unix() ?? 0)
      ),
    [assesmentLinks.data]
  );

  if (assesmentLinks.isLoading) {
    return <Skeleton active />;
  }

  const latestLink = assessmentLinkData.at(0);

  return (
    <ErrorListContext value={{ errorList }}>
      <Flex vertical gap={16} align="center">
        {
          /**
           * If there is at least one link
           */ assessmentLinkData.length >= 1 && (
            <DisplayAssessmentLink
              assessmentLinks={
                assessmentLinkData as [
                  PatientAssessmentLink,
                  ...PatientAssessmentLink[]
                ]
              }
            />
          )
        }
        <Flex vertical align="center" style={{ width: '100%' }}>
          {latestLink === undefined || latestLink.status !== 'pending' ? (
            <Flex vertical gap={16} align="center">
              {latestLink !== undefined && (
                <Text>
                  The last assessment link is {latestLink.status}. To send a new
                  link to your patient, please generate a new link.
                </Text>
              )}
              <div>
                <BaseButton
                  loading={mutationActions.create.isLoading}
                  onClick={() => createAssessmentLink()}
                >
                  Generate new link
                </BaseButton>
              </div>
            </Flex>
          ) : (
            <CopyInviteLinkButton link={latestLink} />
          )}
        </Flex>

        {mutationActions.create.error && (
          <ErrorDisplay error={resolveError(mutationActions.create.error)} />
        )}

        <ErrorListDisplay errorList={errorList} />
      </Flex>
    </ErrorListContext>
  );
}

function CopyInviteLinkButton(props: { link: PatientAssessmentLink }) {
  const copyToClipboard = useCopyToClipboardMutation();
  function copyToClipboardWrapper(content: string) {
    copyToClipboard.mutate(content, {
      onSuccess() {
        notification.success({
          message:
            'Copied link to Clipboard. This is a sensitive link that will allow the recipient to start a ConcussionRx Assessment as the specified Test-Taker. ' +
            'Do not share it with anyone other than the intended Test-Taker.',
          showProgress: true,
          duration: 5
        });
      }
    });
  }

  const a = useAssessmentLinkGenerateToken(props.link.id);
  if (a.isLoading) {
    return <Spin />;
  }

  const link = tokenToInviteLink(a.data?.data ?? '');
  return (
    <Flex vertical gap={16} style={{ width: '100%' }} align="center">
      <Text>Copyable invite link</Text>
      <div style={{ width: '50%' }}>
        <Form.Item>
          <Input
            data-private="redact"
            readOnly
            value={link}
            suffix={
              <CopyOutlined onClick={() => copyToClipboardWrapper(link)} />
            }
          />
        </Form.Item>
      </div>
      <div>
        <BaseButton
          loading={copyToClipboard.isPending}
          onClick={() => copyToClipboardWrapper(link)}
        >
          Copy link
        </BaseButton>
      </div>
    </Flex>
  );
}

function DisplayAssessmentLink(props: {
  assessmentLinks: [PatientAssessmentLink, ...PatientAssessmentLink[]];
}) {
  return (
    <Flex gap={16} vertical align="center" style={{ width: '100%' }}>
      <div style={{ width: '100%' }}>
        <DisplayAssessmentLinksTable assessmentLinks={props.assessmentLinks} />
      </div>
    </Flex>
  );
}
